import { useEffect, useState } from "react";

import { Layout } from "../layout";
import { useGetMenuCategories } from "./use-get-menu-categories";
import { VisibilityIcon } from "../../components/atoms/visibility-icon";
import { useUpdateMenu } from "./use-update-menu";
import { Button } from "@/components/atoms/button/button";
import { MenuPreview } from "./menu-preview";
import Loading from "@/components/atoms/loading";
import { useConfirmDialog } from "@/hooks/use-confirm-dialog";

export function VirtualMenuPage() {
	const { categories, loading } = useGetMenuCategories();
	const [menuData, setMenuData] = useState<typeof categories>([]);
	const { execute: updateMenu, loading: updating } = useUpdateMenu();
	const { confirm } = useConfirmDialog();

	useEffect(() => {
		setMenuData(window.structuredClone(categories));
	}, [categories]);

	function updateVisibilityInMenu(
		categoryId: string,
		type: "category" | "item" | "group" | "group-item",
		id?: string,
	) {
		setMenuData((prevMenuData) => {
			const newMenuData = window.structuredClone(prevMenuData);
			const category = newMenuData.find((cat) => cat.id === categoryId);
			if (!category) return newMenuData;

			if (type === "category") {
				category.showInMenu = !category.showInMenu;
				return newMenuData;
			}

			if (type === "item") {
				const item = category.items.find((it) => it.id === id);
				if (item) {
					item.showInMenu = !item.showInMenu;
				}
				return newMenuData;
			}

			if (type === "group") {
				const group = category.items.find((it) => it.id === id);
				if (group) {
					group.showInMenu = !group.showInMenu;
				}
			}

			if (type === "group-item") {
				for (const item of category.items) {
					if ("items" in item) {
						const groupItem = item.items.find((gi) => gi.id === id);
						if (groupItem) {
							groupItem.showInMenu = !groupItem.showInMenu;
							break;
						}
					}
				}
			}

			return newMenuData;
		});
	}

	return (
		<Layout>
			{updating && <Loading />}
			<h1>Carta virtual</h1>
			{loading && <p>Cargando...</p>}
			<section className="row">
				<div className="col-6">
					<p>Configuración</p>
					{menuData.map((category) => (
						<div key={category.id}>
							<h2 className="d-flex align-items-center gap-2">
								<VisibilityIcon
									isVisible={category.showInMenu}
									onClick={() =>
										updateVisibilityInMenu(category.id, "category")
									}
								/>
								{category.name}
							</h2>
							<ul>
								{category.items.map((item) => {
									if ("showAs" in item) {
										// It's a group
										const itemsAreHidden = item.showAs === "none";
										return (
											<li key={item.id} className="list-unstyled">
												<div className="d-flex align-items-center gap-2">
													<VisibilityIcon
														isVisible={item.showInMenu}
														onClick={() =>
															updateVisibilityInMenu(
																category.id,
																"group",
																item.id,
															)
														}
													/>
													{item.name}
												</div>
												<ul>
													{item.items.map((groupItem) => (
														<li
															key={groupItem.id}
															className="d-flex align-items-center gap-2"
														>
															{!itemsAreHidden && (
																<VisibilityIcon
																	isVisible={groupItem.showInMenu}
																	onClick={() =>
																		updateVisibilityInMenu(
																			category.id,
																			"group-item",
																			groupItem.id,
																		)
																	}
																/>
															)}
															{groupItem.product.displayName} - S/
															{groupItem.product.price}
														</li>
													))}
												</ul>
											</li>
										);
									}
									// It's a single item
									return (
										<li
											key={item.id}
											className="d-flex align-items-center gap-2"
										>
											<VisibilityIcon
												isVisible={item.showInMenu}
												onClick={() =>
													updateVisibilityInMenu(category.id, "item", item.id)
												}
											/>
											{item.product.displayName} - S/{item.product.price}
										</li>
									);
								})}
							</ul>
						</div>
					))}
					<Button
						disabled={updating}
						onClick={async () => {
							const res = await confirm({
								type: "question",
								title: "Confirmar actualización",
								content:
									"¿Estás seguro de que deseas actualizar la carta virtual?",
							});

							if (!res) return;

							try {
								updateMenu(menuData);
							} catch (error) {
								const message =
									error instanceof Error ? error.message : "Error desconocido";
								await confirm({
									type: "error",
									title: "Error",
									content: message,
								});
							}
						}}
					>
						Guardar cambios
					</Button>
				</div>
				<MenuPreview menuData={menuData} />
			</section>
		</Layout>
	);
}
